import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser } from '../../api/Index';
import { FiEye, FiEyeOff } from "react-icons/fi";
import './CreateUser.css';
import PageTitle from '../../components/UI/PageTitle';

const CreateUser = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'member',
    emergencyContact: {
      name: '',
      contact: ''
    },
    beneficiary: {
      name: '',
      contact: ''
    },
    currentLegacyCredits: 0
  });
  
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setError(null);
  };

  const handleRoleChange = (role) => {
    setFormData({
      ...formData,
      role
    });
    setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    
    setCreating(true);
    try {
      const response = await createUser(formData);
      
      // Redirect to users page
      navigate('/users');
      
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to create user');
      setCreating(false);
    }
  };
  
  return (
    <div>
        <PageTitle title='Create User' />
        <div className="create-user-container">
            <div className="form-container">
                {error && (
                    <div style={{
                        backgroundColor: '#ffe6e6',
                        border: '1px solid #ff0000',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '20px',
                        color: '#cc0000'
                    }}>
                        {error}
                    </div>
                )}
                
                
                <form className="create-user-form" onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label className="input-label">Name *</label>
                        <input
                            type="text"
                            name="name"
                            className="input"
                            placeholder="Enter full name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Email *</label>
                        <input
                            type="email"
                            name="email"
                            className="input"
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Phone</label>
                        <input
                            type="tel"
                            name="phone"
                            className="input"
                            placeholder="Enter phone number"
                            value={formData.phone}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className='role-group'>
                        <label className="input-label">Role</label>
                        <div className='role-label-group'>
                            <button
                                type="button"
                                onClick={() => handleRoleChange('member')}
                                style={{
                                    backgroundColor: formData.role === 'member' ? '#007bff' : '#f8f9fa',
                                    color: formData.role === 'member' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Member
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRoleChange('admin')}
                                style={{
                                    backgroundColor: formData.role === 'admin' ? '#007bff' : '#f8f9fa',
                                    color: formData.role === 'admin' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Admin
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Password *</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                className="input"
                                placeholder="Enter password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                style={{ paddingRight: '50px' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '15px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: '18px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Emergency Contact Name</label>
                        <input
                            type="text"
                            className="input"
                            name="emergencyContact.name"
                            value={formData.emergencyContact.name}
                            onChange={handleInputChange}
                            placeholder="Enter emergency contact name"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Emergency Contact Phone</label>
                        <input
                            type="tel"
                            className="input"
                            name="emergencyContact.contact"
                            value={formData.emergencyContact.contact}
                            onChange={handleInputChange}
                            placeholder="Enter emergency contact phone"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Beneficiary Name</label>
                        <input
                            type="text"
                            className="input"
                            name="beneficiary.name"
                            value={formData.beneficiary.name}
                            onChange={handleInputChange}
                            placeholder="Enter beneficiary name"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Beneficiary Contact</label>
                        <input
                            type="tel"
                            className="input"
                            name="beneficiary.contact"
                            value={formData.beneficiary.contact}
                            onChange={handleInputChange}
                            placeholder="Enter beneficiary contact"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Legacy Credits</label>
                        <input
                            type="number"
                            className="input"
                            name="currentLegacyCredits"
                            value={formData.currentLegacyCredits}
                            onChange={handleInputChange}
                            placeholder="Enter legacy credits"
                            min="0"
                        />
                    </div>

                    <button 
                        type="submit"
                        className='form-button'
                        disabled={creating}
                        style={{
                            opacity: creating ? 0.6 : 1,
                            cursor: creating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {creating ? 'Creating User...' : 'Create User'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default CreateUser;